/**
 * AddTemplateActionDialog - Dialog para adicionar/editar ação de envio de template WhatsApp
 * 
 * Layout padronizado com SendTemplateDialog do chat:
 * - Step 1: Seleção com TemplateCard
 * - Step 2: Grid 2 colunas (Preview WhatsApp + Variáveis)
 * 
 * @version 2.0.0
 * @date 2026-03-08
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  FileText,
  Loader2,
  ChevronLeft,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Globe,
  FileSignature,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import { TemplateCard } from '@/components/tenant/templates/TemplateCard';

const logger = createLogger('AddTemplateActionDialog');

// =====================================================
// INTERFACES
// =====================================================

/** Estrutura do action_value para send_template */
interface TemplateActionData {
  inbox_id: string;
  template_name: string;
  template_language: string;
  template_params: {
    body_params: string[];
    header_params: string[];
    button_params: string[];
  };
}

/** Template WhatsApp simplificado */
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  components: any;
  created_at?: string;
  classification_id?: string | null;
}

/** Inbox com provider slug */
interface InboxOption {
  id: string;
  name: string;
  provider_slug: string;
}

interface AddTemplateActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: TemplateActionData) => void;
  initialValue?: Record<string, unknown>;
  /** IDs das inboxes selecionadas na regra pai (filtra templates) */
  selectedInboxIds?: string[];
}

/** Variáveis disponíveis no contexto de roteamento */
const ROUTING_VARIABLES = [
  { value: '{{nome}}', label: 'Nome do contato' },
  { value: '{{telefone}}', label: 'Telefone do contato' },
];

/**
 * Extrai variáveis {{1}}, {{2}} do texto de um componente
 */
function extractTemplateVariables(text: string): string[] {
  if (!text) return [];
  const matches = text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches)].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''));
    const numB = parseInt(b.replace(/\D/g, ''));
    return numA - numB;
  });
}

/**
 * Extrai componente por tipo do array de components
 */
function getComponent(components: any[], type: string) {
  if (!components || !Array.isArray(components)) return null;
  return components.find((c: any) => c.type === type);
}

/**
 * Substitui variáveis pelos valores fornecidos (formato array indexado)
 */
function replaceVariablesFromArray(text: string, variables: string[], values: string[]): string {
  if (!text) return '';
  let result = text;
  variables.forEach((v, i) => {
    const val = values[i];
    if (val) {
      result = result.replace(v, val);
    }
  });
  return result;
}

// Configurações de status para o TemplateCard
const statusConfig = {
  APPROVED: { label: 'Aprovado', icon: CheckCircle2, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  PENDING: { label: 'Pendente', icon: Clock, color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  REJECTED: { label: 'Rejeitado', icon: XCircle, color: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

// Configurações de categoria para o TemplateCard
const categoryConfig = {
  MARKETING: { label: 'Marketing', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  UTILITY: { label: 'Utilitário', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  AUTHENTICATION: { label: 'Autenticação', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
};

// Labels em português para categorias
const categoryLabels: Record<string, string> = {
  UTILITY: 'Utilitário',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
};

// Cores por categoria
const categoryColors: Record<string, string> = {
  MARKETING: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  UTILITY: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  AUTHENTICATION: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function AddTemplateActionDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
  selectedInboxIds,
}: AddTemplateActionDialogProps) {
  const { currentTenant } = useAuth();

  // Estados de navegação
  const [step, setStep] = useState<'template' | 'params'>('template');

  // Estado de dados
  const [inboxes, setInboxes] = useState<InboxOption[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Estado de parâmetros
  const [bodyParamValues, setBodyParamValues] = useState<string[]>([]);
  const [headerParamValues, setHeaderParamValues] = useState<string[]>([]);

  // Estado de carregamento
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // =====================================================
  // EFEITOS
  // =====================================================

  // Carrega templates ao abrir
  useEffect(() => {
    if (open && currentTenant?.id) {
      fetchTemplatesFromSelectedInboxes();
    }
  }, [open, currentTenant?.id]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setStep('template');
      setSelectedInboxId('');
      setSelectedTemplate(null);
      setSearchQuery('');
      setBodyParamValues([]);
      setHeaderParamValues([]);
    }
  }, [open]);

  // Restaurar valores ao editar
  useEffect(() => {
    if (open && initialValue?.inbox_id && templates.length > 0) {
      if (initialValue.template_name) {
        const found = templates.find(
          (t: WhatsAppTemplate) => t.name === initialValue.template_name && t.language === initialValue.template_language
        );
        if (found) {
          setSelectedInboxId(found.inbox_id);
          handleSelectTemplate(found, true);
        }
      }
    }
  }, [open, initialValue, templates]);

  // =====================================================
  // BUSCA DE DADOS
  // =====================================================

  /** Busca templates aprovados das inboxes selecionadas na regra */
  const fetchTemplatesFromSelectedInboxes = async () => {
    if (!currentTenant?.id) return;
    setLoadingTemplates(true);
    try {
      // Busca todos os templates aprovados do tenant via RPC server-side
      // (filtra por inbox client-side quando há inboxes selecionadas)
      const { data, error } = await supabase.rpc('get_tenant_whatsapp_templates_for_routing', {
        p_inbox_id: null,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;

      const all = (Array.isArray(data) ? data : []) as Array<any>;
      const filtered = (selectedInboxIds && selectedInboxIds.length > 0)
        ? all.filter(t => selectedInboxIds.includes(t.inbox_id))
        : all;

      setTemplates(filtered as any);

      // Buscar inboxes do tenant via RPC segura para exibir nome/provider
      const inboxIds = [...new Set(filtered.map(t => t.inbox_id))];
      if (inboxIds.length > 0) {
        const { data: inboxData } = await supabase.rpc('get_tenant_inboxes_safe', {
          p_tenant_hint: currentTenant.id,
        });

        const mapped: InboxOption[] = ((inboxData || []) as Array<any>)
          .filter((inbox) => inboxIds.includes(inbox.id))
          .map((inbox) => ({
            id: inbox.id,
            name: inbox.name,
            // RPC segura não expõe slug; usa display_name como fallback
            provider_slug: inbox.provider_display_name || 'desconhecido',
          }));
        setInboxes(mapped);
      }

      logger.info('Templates carregados das inboxes selecionadas', { count: filtered.length });
    } catch (error) {
      logger.error('Erro ao carregar templates', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  /** Seleciona template e avança para parâmetros */
  const handleSelectTemplate = (template: WhatsAppTemplate, isRestore = false) => {
    setSelectedTemplate(template);
    setSelectedInboxId(template.inbox_id);

    // Extrair variáveis do body
    const bodyComponent = getComponent(template.components, 'BODY');
    const bodyText = bodyComponent?.text || '';
    const bodyVars = extractTemplateVariables(bodyText);

    // Extrair variáveis do header
    const headerComponent = getComponent(template.components, 'HEADER');
    const headerText = headerComponent?.text || '';
    const headerVars = extractTemplateVariables(headerText);

    // Se restaurando valores, usa os salvos; senão inicia vazio
    if (isRestore && initialValue?.template_params) {
      const params = initialValue.template_params as any;
      setBodyParamValues(params.body_params || bodyVars.map(() => ''));
      setHeaderParamValues(params.header_params || headerVars.map(() => ''));
    } else {
      setBodyParamValues(bodyVars.map(() => ''));
      setHeaderParamValues(headerVars.map(() => ''));
    }

    setStep('params');
  };

  /** Handler para voltar à seleção */
  const handleBack = () => {
    setStep('template');
    setSelectedTemplate(null);
    setBodyParamValues([]);
    setHeaderParamValues([]);
  };

  /** Salva a ação de template */
  const handleSave = () => {
    if (!selectedTemplate || !selectedInboxId) return;

    const data: TemplateActionData = {
      inbox_id: selectedInboxId,
      template_name: selectedTemplate.name,
      template_language: selectedTemplate.language,
      template_params: {
        body_params: bodyParamValues,
        header_params: headerParamValues,
        button_params: [],
      },
    };

    onSave(data);
    onOpenChange(false);
  };

  // =====================================================
  // DERIVADOS
  // =====================================================

  /** Templates filtrados por busca */
  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(t =>
      t.name.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q)
    );
  }, [templates, searchQuery]);

  /** Texto do body do template selecionado */
  const bodyText = useMemo(() => {
    if (!selectedTemplate?.components) return '';
    const body = getComponent(selectedTemplate.components, 'BODY');
    return body?.text || '';
  }, [selectedTemplate]);

  /** Variáveis do body do template selecionado */
  const bodyVariables = useMemo(() => {
    return extractTemplateVariables(bodyText);
  }, [bodyText]);

  /** Variáveis do header do template selecionado */
  const headerVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    const headerComponent = getComponent(selectedTemplate.components, 'HEADER');
    return extractTemplateVariables(headerComponent?.text || '');
  }, [selectedTemplate]);

  /** Preview do body com variáveis substituídas em tempo real */
  const previewText = useMemo(() => {
    return replaceVariablesFromArray(bodyText, bodyVariables, bodyParamValues);
  }, [bodyText, bodyVariables, bodyParamValues]);

  /** Total de parâmetros a preencher */
  const totalParams = bodyVariables.length + headerVariables.length;

  /** Verifica se pode salvar */
  const canSave = selectedTemplate && selectedInboxId;

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {step === 'template' ? 'Selecionar Template' : `Configurar: ${selectedTemplate?.name}`}
          </DialogTitle>
        </DialogHeader>

        {/* ========== STEP 1: SELECIONAR TEMPLATE (igual SendTemplateDialog) ========== */}
        {step === 'template' && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Campo de busca */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Lista de templates com TemplateCard */}
            <ScrollArea className="h-[calc(70vh-180px)]">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mb-2" />
                  <p className="font-medium">Nenhum template encontrado</p>
                  <p className="text-sm">
                    {templates.length === 0
                      ? 'Sincronize os templates na página de Templates'
                      : 'Tente outra busca'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 pb-4 px-1">
                  {filteredTemplates.map((template) => {
                    // Nome da inbox de origem para exibição
                    const inboxName = inboxes.length > 1
                      ? inboxes.find(i => i.id === template.inbox_id)?.name
                      : undefined;

                    return (
                      <div
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className="cursor-pointer"
                      >
                        <TemplateCard
                          template={{
                            ...template,
                            template_id: template.id,
                            rejection_reason: null,
                            variables: null,
                            example_values: null,
                            provider_metadata: null,
                            created_at: template.created_at || new Date().toISOString(),
                            submitted_at: null,
                            approved_at: null,
                            classification_id: template.classification_id || null,
                          }}
                          providerName={inboxName}
                          statusConfig={statusConfig}
                          categoryConfig={categoryConfig}
                          onEdit={() => {}}
                          onPreview={() => handleSelectTemplate(template)}
                          onDelete={() => {}}
                          canEdit={false}
                          canDelete={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* ========== STEP 2: CONFIGURAR PARÂMETROS (layout 2 colunas igual SendTemplateDialog) ========== */}
        {step === 'params' && selectedTemplate && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Header com badges de categoria/idioma/status */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-muted/30">
                  <Badge 
                    variant="outline" 
                    className={cn("text-[10px] shrink-0 px-2 py-0.5", categoryColors[selectedTemplate.category])}
                  >
                    {categoryLabels[selectedTemplate.category] || selectedTemplate.category}
                  </Badge>
                  <div className="flex items-center gap-1 text-muted-foreground shrink-0">
                    <Globe className="h-3 w-3" />
                    <span className="text-xs">{selectedTemplate.language}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0 px-2 py-0.5 bg-green-500/10 text-green-600 border-green-500/20">
                    <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                    APROVADO
                  </Badge>
                </div>
              </div>
            </div>

            {/* Grid 2 colunas: Preview + Variáveis */}
            <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
              {/* Coluna esquerda: Preview estilo WhatsApp */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium shrink-0">Preview da Mensagem</Label>
                <div className="rounded-xl bg-[#e5ddd5] p-3 max-h-[200px] overflow-auto">
                  <div className="max-w-[220px]">
                    <div className="bg-white rounded-lg shadow-sm p-2">
                      <p className="text-xs whitespace-pre-wrap break-words">
                        {previewText || bodyText}
                      </p>
                      <div className="text-right mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {new Date().toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dica de variáveis de roteamento */}
                <div className="space-y-1.5 mt-2">
                  <p className="text-xs font-medium text-muted-foreground">Variáveis disponíveis:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ROUTING_VARIABLES.map((rv) => (
                      <Badge
                        key={rv.value}
                        variant="outline"
                        className="text-xs cursor-pointer hover:bg-muted/50"
                        title={rv.label}
                      >
                        {rv.value}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Use nos campos ao lado para inserir dados dinâmicos.
                  </p>
                </div>
              </div>

              {/* Coluna direita: Variáveis com scroll */}
              <div className="flex flex-col gap-2 min-h-0 flex-1">
                <Label className="text-sm font-medium shrink-0">
                  Variáveis {totalParams > 0 && `(${totalParams})`}
                </Label>
                <ScrollArea className="flex-1 min-h-0">
                  <div className="pr-3 space-y-4">
                    {/* Variáveis do Header */}
                    {headerVariables.length > 0 && (
                      <div className="space-y-3">
                        <div className="text-xs text-muted-foreground font-medium">
                          Header
                        </div>
                        {headerVariables.map((v, i) => (
                          <div key={`header-${i}`} className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Valor para {v}
                            </Label>
                            <Input
                              placeholder={`Valor para ${v} (ex: {{nome}})`}
                              value={headerParamValues[i] || ''}
                              onChange={(e) => {
                                const newValues = [...headerParamValues];
                                newValues[i] = e.target.value;
                                setHeaderParamValues(newValues);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Variáveis do Body */}
                    {bodyVariables.length > 0 && (
                      <div className="space-y-3">
                        {headerVariables.length > 0 && <Separator />}
                        <div className="text-xs text-muted-foreground font-medium">
                          Mensagem
                        </div>
                        {bodyVariables.map((v, i) => (
                          <div key={`body-${i}`} className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Valor para {v}
                            </Label>
                            <Input
                              placeholder={`Valor para ${v} (ex: {{nome}})`}
                              value={bodyParamValues[i] || ''}
                              onChange={(e) => {
                                const newValues = [...bodyParamValues];
                                newValues[i] = e.target.value;
                                setBodyParamValues(newValues);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Estado vazio - sem variáveis */}
                    {totalParams === 0 && (
                      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                        Este template não possui variáveis
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>

            <Separator />

            {/* Footer com ações (igual SendTemplateDialog) */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Voltar
              </Button>
              <Button onClick={handleSave} disabled={!canSave}>
                <FileSignature className="h-4 w-4 mr-2" />
                Salvar Template
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
